// The player: one store for what Spotify is playing, polled while a page
// that shows it is on screen, plus the embed drawer. The app is the remote:
// transport and "play this" go to the server, which talks to Spotify; the
// drawer is Spotify's own embed for previews when nothing is playing
// anywhere or the account is not Premium.
import { create } from 'zustand'
import { api } from '@/runtime'
import type { PlayRequest, PlayerAction, PlayerState } from '@/db/types'

interface PlayerStore {
  state: PlayerState | null
  error: string | null
  /** why the last poll failed (cleared by the next good one), so a page with no state yet can say so */
  lost: string | null
  /** how many components want the poll; the loop runs while > 0 */
  watchers: number
  /** the embed drawer: a Spotify URI to preview, or null */
  embed: { uri: string; title?: string } | null
  watch: () => () => void
  /** ask now (Try again on a failed read) */
  retry: () => void
  control: (action: PlayerAction, play?: PlayRequest) => Promise<void>
  play: (play: PlayRequest, title?: string) => Promise<void>
  openEmbed: (uri: string, title?: string) => void
  closeEmbed: () => void
}

let timer: number | null = null
let inFlight = false
/** fast polling until this time: after a command, or when a change is seen */
let burstUntil = 0

const BURST_MS = 4000
const BURST_STEP = 300

/** poll fast for a few seconds: Spotify's state lags a command or a skip on
 *  another device by a moment, and the bar should catch up, not wait a tick */
const burst = () => {
  burstUntil = Date.now() + BURST_MS
  if (timer !== null) {
    window.clearTimeout(timer)
    timer = null
  }
  if (usePlayer.getState().watchers > 0) startLoop()
}

const tick = async () => {
  if (inFlight || document.visibilityState !== 'visible') return
  inFlight = true
  try {
    const st = await api.spotifyPlayer()
    const prev = usePlayer.getState().state
    usePlayer.setState({ state: st, lost: null })
    // a change made elsewhere (phone, speaker): follow it closely for a moment
    if (prev && (prev.playing !== st.playing || prev.url !== st.url)) burstUntil = Date.now() + BURST_MS
  } catch (e) {
    // offline or refused: keep the last state, and say why for a page that has none
    usePlayer.setState({ lost: e instanceof Error ? e.message : 'Spotify did not answer' })
  } finally {
    inFlight = false
  }
}

const delay = () => {
  if (Date.now() < burstUntil) return BURST_STEP
  // once a second while something is playing, every few seconds otherwise
  return usePlayer.getState().state?.playing ? 1000 : 4000
}

const startLoop = () => {
  if (timer !== null) return
  void tick()
  const step = () => {
    timer = window.setTimeout(async () => {
      await tick()
      if (usePlayer.getState().watchers > 0) step()
      else timer = null
    }, delay())
  }
  step()
}

const stopLoop = () => {
  if (timer !== null) window.clearTimeout(timer)
  timer = null
}

export const usePlayer = create<PlayerStore>((set, get) => ({
  state: null,
  error: null,
  lost: null,
  watchers: 0,
  embed: null,
  watch: () => {
    set((s) => ({ watchers: s.watchers + 1 }))
    startLoop()
    return () => {
      set((s) => ({ watchers: Math.max(0, s.watchers - 1) }))
      if (get().watchers === 0) stopLoop()
    }
  },
  retry: () => burst(),
  control: async (action, play) => {
    try {
      const st = await api.spotifyControl(action, play)
      // a seek is believed at once: Spotify reports the old place for a beat
      const shown =
        action === 'seek' && play?.position_ms !== undefined
          ? { ...st, progress_ms: play.position_ms, fetched_at: new Date().toISOString() }
          : st
      set({ state: shown, error: null })
      burst()
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Spotify refused' })
      throw e
    }
  },
  play: async (play, title) => {
    const st = get().state
    // no connection, or nothing Spotify can drive: fall back to the embed for a preview
    if (!st?.connected) {
      if (play.uri) get().openEmbed(play.uri, title)
      else set({ error: 'Connect Spotify in Settings to play from here.' })
      return
    }
    try {
      await get().control('play', play)
    } catch (e) {
      // Premium-only or no device: the embed still previews a track
      if (play.uri) get().openEmbed(play.uri, title)
      else throw e
    }
  },
  openEmbed: (uri, title) => set({ embed: { uri, title } }),
  closeEmbed: () => set({ embed: null }),
}))
